/* eslint-disable @typescript-eslint/naming-convention */
import { Bundle, BundleType, IProduct, ProductData, productDataTransformer } from '@commerce-product-webcomponents/sdk';
import type { CheckoutCardPropsTypes } from '@commerce-product-webcomponents/ui';
import { type AppState, formatPrice } from '@nextgen-web-framework/core';
import type { ICheckoutOrderSummaryData } from '@commerce-checkout-webcomponents/ui';
import {
  GetFulfillmentProps,
  BundleItem,
  LineItem,
  FulfillmentTypeCollection,
} from '@commerce-fulfillment-webcomponents/sdk';
import type {
  DeliveryAddress,
  DeliveryOption,
  Fulfillment,
  PlanResponse,
  SubscriptionProductEntry,
  SubscriptionResponse,
} from '../services/models';
import { SubscriptionType, SubscriptionResponseStatusEnum } from '../services/models';
import {
  FulfillmentType,
  ISubscriptionConfirmation,
  PlanBriefDescriptionDetails,
  PlanDetailsTopBanner,
  StartSubscription,
} from '../types/subscriptionTypes';
import { BundleSlot } from '../services/models/BundleSlot';
import { FulfillmentTypeIdEnum, PlanResponseTypeEnum } from '../types';

export const productId = (id: string) => {
  const productIdArr = id.split('-');
  return productIdArr.length > 1 ? productIdArr[1] : productIdArr[0];
};

export const nullCheck = (val: AppState, defaultVal: AppState) => val ?? defaultVal;

type SubscriptionSummaryTransformerType = {
  summary: ICheckoutOrderSummaryData & { totalPrice: number };
  standerdShippingFee?: DeliveryOption;
};

export const subscriptionSummaryTransformer = (
  productData: ProductData[],
  subscription: SubscriptionResponse,
  fulfillmentData?: FulfillmentTypeCollection,
): SubscriptionSummaryTransformerType => {
  let total = 0,
    productCurrency = '',
    totalPointValue = 0,
    totalBusinessVolume = 0;

  const productEntries: AppState = [];

  productData.forEach((product) => {
    let pointValue = 0,
      businessVolume = 0,
      productPrice = 0;
    const transformedProduct = productDataTransformer(product);
    productCurrency = transformedProduct.currency;
    const isGmnPlan = subscription?.plan?.type === PlanResponseTypeEnum.GMN;
    let productQuantity = 0;

    const calculateTotals = (quantity: number, price: string, pv: string, bv: string) => {
      productQuantity = quantity;
      total += quantity * parseInt(price || '0');
      pointValue += quantity * parseInt(pv || '0');
      businessVolume += quantity * parseInt(bv || '0');
      productPrice += quantity * parseInt(price || '0');
      totalPointValue += pointValue;
      totalBusinessVolume += businessVolume;
    };

    const matchingEntry = subscription.entries?.find(
      (entry) => productId(entry.product.id ?? '') === productId(transformedProduct.productId),
    );

    if (!isGmnPlan) {
      const productPrice = transformedProduct.isBundle
        ? transformedProduct.minPrice ?? 0
        : transformedProduct.productPrice ?? 0;

      if (matchingEntry) {
        calculateTotals(
          matchingEntry.quantity,
          `${productPrice}`,
          `${transformedProduct.pointValue}`,
          `${transformedProduct.businessVolume}`,
        );
      }
    } else if (!subscription?.currentElements?.length) {
      if (matchingEntry) {
        const itemFixedPrice = subscription?.plan?.elementTemplates?.[0]?.coupons?.[0]?.itemFixedPrices?.[0] ?? {};
        calculateTotals(
          matchingEntry.quantity,
          itemFixedPrice.price ?? '0',
          itemFixedPrice.pvValue ?? '0',
          itemFixedPrice.bvValue ?? '0',
        );
      }
    } else {
      const currentElement = subscription.currentElements?.find((element) => !element?.isCompensated);
      if (matchingEntry && currentElement) {
        const itemFixedPrice = currentElement.itemFixedPrices?.[0] ?? {};
        calculateTotals(
          matchingEntry.quantity,
          itemFixedPrice.price ?? '0',
          itemFixedPrice.pvValue ?? '0',
          itemFixedPrice.bvValue ?? '0',
        );
      }
    }

    productEntries.push({
      brandName: transformedProduct.brandName,
      productName: transformedProduct.productName,
      link: nullCheck(transformedProduct?.mainLink, ''),
      currency: transformedProduct.currency,
      productId: transformedProduct.productId,
      productPrice: productPrice,
      productQuantity: productQuantity,
      varient: '',
      alt: transformedProduct.alt,
      pointValue,
      businessVolume,
    });
  });

  const isManualSOP = subscription.type === SubscriptionType.Manual;
  let deliveryOption: undefined | DeliveryOption = undefined,
    shipping = {
      shippingFee: 0,
      freeShipping: true,
    };

  if (!isManualSOP && fulfillmentData) {
    deliveryOption = transformShippingFee(fulfillmentData, total, !!subscription.plan?.isFreeDelivery);
    const { shippingFee, freeShipping } = getShippingFee(subscription, deliveryOption);
    shipping = { shippingFee, freeShipping };
  }

  return {
    summary: {
      totalPrice: total,
      totalItems: productData.length,
      totalAmount: `${formatPrice(total + shipping.shippingFee, productCurrency)}`,
      productEntries: productEntries,
      subTotal: formatPrice(total, productCurrency),
      shippingFee: formatPrice(shipping.shippingFee, productCurrency),
      freeShipping: shipping.freeShipping,
      taxes: {
        value: 0,
        taxDetails: [],
      },
      placeOrderDisabled: true,
      pvbvValue: `${totalPointValue}/${totalBusinessVolume}`,
    },
    standerdShippingFee: deliveryOption,
  };
};

const getShippingFee = (subscription: SubscriptionResponse, options?: DeliveryOption) => {
  let shippingFee = options ? parseInt(nullCheck(options.deliveryCharge, '0')) : 0;
  let freeShipping = !(options?.deliveryCharge && options?.deliveryCharge != '0');

  if (subscription.plan?.isFreeDelivery) {
    shippingFee = 0;
    freeShipping = true;
  }

  return { shippingFee, freeShipping };
};

export const getProductsByIdFromBundle = (bundle: Bundle) =>
  bundle?.slots?.reduce<Record<string, ProductData>>((acc, slot) => {
    slot.products?.forEach?.((product) => {
      if (product?.variantGroup?.optionMap?.length) {
        product.variantGroup.optionMap.forEach((option) => {
          if (option.product?.productId) {
            const transformedProduct = productDataTransformer(option.product as ProductData);
            if (transformedProduct.productId) {
              acc[transformedProduct.productId] = { ...option.product } as ProductData;
            }
          }
        });
      } else {
        const transformedProduct = productDataTransformer(product);
        if (transformedProduct.productId) {
          acc[transformedProduct.productId] = { ...product };
        }
        if (product.id) {
          acc[product.id] = { ...product };
        }
      }
    });
    return acc;
  }, {}) ?? {};

export const calculateDynamicBundlePriceSummary = (bundle: Bundle, subscriptionEntry: SubscriptionProductEntry) => {
  const slots = subscriptionEntry?.bundleSlots || [];
  type SummaryType = { price: number; pointValue: number; businessVolume: number };
  const summary: SummaryType = { price: 0, pointValue: 0, businessVolume: 0 };

  const productByIds = getProductsByIdFromBundle(bundle);

  slots.forEach((slot) => {
    const priceSummary = slot?.entries?.reduce(
      (acc: SummaryType, entry) => {
        const productData = productDataTransformer(productByIds[entry.product?.id as string]);
        const quantity = nullCheck(entry.quantity, 0);
        const data = {
          price: acc.price + productData.productPrice * quantity,
          businessVolume: acc.businessVolume + parseInt(productData.businessVolume || '0') * quantity,
          pointValue: acc.pointValue + parseInt(productData.pointValue || '0') * quantity,
        };

        return data;
      },
      { price: 0, pointValue: 0, businessVolume: 0 },
    ) || { ...summary };
    summary.price = summary.price + priceSummary?.price;
    summary.businessVolume = summary.businessVolume + priceSummary?.businessVolume;
    summary.pointValue = summary.pointValue + priceSummary?.pointValue;
  });

  return summary;
};

export const transformSubscriptionProducts = (productData: ProductData[], entries: SubscriptionProductEntry[]) => {
  const productEntries: CheckoutCardPropsTypes[] = [];
  productData.forEach((product) => {
    const transformedProduct = productDataTransformer(product);

    let productPrice = nullCheck(transformedProduct.productPrice, transformedProduct.minPrice);
    let productQuantity = 0;

    for (const entry of entries) {
      if (productId(nullCheck(entry.product.id, '')) == productId(transformedProduct.productId)) {
        const { quantity } = entry;
        productQuantity = quantity;
        if (transformedProduct.isBundle && transformedProduct.bundle?.type == BundleType.Variable) {
          const bundlePrice = calculateDynamicBundlePriceSummary(transformedProduct.bundle, entry);
          transformedProduct.pointValue = bundlePrice.pointValue.toString();
          transformedProduct.businessVolume = bundlePrice.businessVolume.toString();
          productPrice = bundlePrice.price;
        }
        break;
      }
    }
    productEntries.push({
      ...transformedProduct,
      link: nullCheck(transformedProduct?.mainLink, ''),
      productPrice,
      minPrice: productPrice,
      maxPrice: productPrice,
      pointValue: undefined,
      businessVolume: undefined,
      productQuantity: productQuantity,
    });
  });
  return productEntries;
};

export const subscriptionConfirmationTransformer = (response: SubscriptionResponse): ISubscriptionConfirmation => {
  const address = response?.profile?.deliveryAddress ? transformAddress(response?.profile?.deliveryAddress) : '';

  const data = {
    id: response.id,
    subscriptionId: response?.code ?? '',
    subscriptionProfile: response?.profile?.name,
    totalAmount: getSubscriptionTotal(response).totalAmount,
    shippingDetails: {
      deliveryAddress: address,
    },
    minimumNumberOfDays: response?.deliveryOption?.minimumNumberOfDays ?? 0,
    maximumNumberOfDays: response?.deliveryOption?.maximumNumberOfDays ?? 0,
    deliveryInstruction: response?.profile?.deliveryAddress?.deliveryInstructions ?? '',
    deliveryOption: 'Standard',
    paidStatus: (response?.profile?.payment?.paymentStatus as string) ?? 'INITIATED',
    processDay: response?.processDay ?? 0,
    planDescription: response?.plan?.cycle?.description ?? '',
    userName: `${response?.profile?.amwayUser?.firstName ?? ''} ${response?.profile?.amwayUser?.lastName ?? ''}`,
    userEmail: response?.amwayUser?.email ?? '',
    userMobileNo: response?.profile?.deliveryAddress?.phone ?? '',
    paymentId: response?.profile?.payment?.paymentId as string,
    subscriptionType: response.type ?? SubscriptionType.Automatic,
    amwayUserId: response?.amwayUser?.accountId,
    isDownline: response?.isDownline ?? false,
    products: response?.products,
    entries: response?.entries,
    nextPaymentDate: response?.nextPaymentDate,
  };

  return data;
};

export const getDefaultPlan = (plans: PlanResponse[]) => {
  if (plans && plans?.length > 0) {
    return plans[0];
  }
  return null;
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const transformSubscriptionPlans = (data: any) => {
  const plansList = Array.isArray(data?.plans_list) ? data.plans_list[0] : data?.plans_list;

  return {
    componentType: 'plans_list',
    title: plansList?.title,
    subTitle: plansList?.sub_title,
    description: plansList?.description,
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    plans: (plansList?.plans || []).map((plan: any, index: number) => ({
      id: index,
      title: plan.display_title,
      description: plan.sub_title,
      highlights: plan.highlights.description,
      link: plan.link,
      images: plan.image.images,
      cardBGColor: plan.card_background_color,
    })),
  };
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const transformHeroBanner = (data: any) => {
  const homeBanner = Array.isArray(data?.home_banner) ? data?.home_banner[0] : data?.home_banner;
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const images: [] = homeBanner?.images?.map((item: any) => {
    const image = {
      title: item.display_title,
      subTitle: item.sub_title,
      backgroundColor: item.background_color,
      overlayGradient: item.overlay_gradient,
      redirectionInfo: {
        text: item.link.text,
        url: item.link.url,
      },
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      imageList: item.image.images.map((image: any) => ({
        url: image.image.url,
        altText: image.alt_text,
        viewport: image.viewport,
      })),
    };
    return image;
  });
  return {
    componentType: 'home_banner',
    images,
  };
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const transformFAQs = (data: any) => {
  const faqs = data?.faqs[0]?.faqs?.faq;
  return {
    componentType: 'faqs',
    displayTitle: data?.faqs[0]?.display_title,
    faqQuestionAnswer: faqs,
  };
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const transformHowtoSubscribe = (data: any) => {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const images: [] = data?.images?.map((image: any) => ({
    url: image.image.url,
    alt_text: image.alt_text,
    viewport: image.viewport,
  }));

  return {
    componentType: 'why_and_how',
    images: images,
  };
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const transformerPlanDetailsTopBanner = (data: any): PlanDetailsTopBanner => {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const bannerImages: [] = data?.image?.images?.map((image: any) => ({
    url: image.image.url,
    alt_text: image.alt_text,
    viewport: image.viewport,
  }));

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const footerImages: [] = data?.footer_image?.images?.map((image: any) => ({
    url: image.image.url,
    alt_text: image.alt_text,
    viewport: image.viewport,
  }));

  return {
    display_title: data?.plan_name || '',
    description: data?.description || '',
    bannerImages: bannerImages,
    footerImages: footerImages,
    backgroundColor: data?.background_color || '',
    fontColor: data?.font_color || '',
  };
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const transformPlanBriefDescription = (data: any): PlanBriefDescriptionDetails => {
  const images = data?.images?.map((image: any) => ({
    url: image.image.url,
    alt_text: image.alt_text,
    viewport: image.viewport,
  }));

  return {
    images: images,
    displayTitle: data.display_title || 'Plan Details',
    shortDescription: data.short_description,
    longDescription: data.long_description,
  };
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const transformStartSubscription = (data: any): StartSubscription => ({
  display_title: data?.start_subscription?.display_title,
  description: data?.start_subscription?.description,
  plan_codes: {
    primary_plan_code: data?.primary_plan_code,
    secondary_plan_codes: data?.secondary_plan_codes,
  },
  rewards_and_benefits: { ...data?.rewards_and_benefits },
});

export const transformAddress = (deliveryAddress: DeliveryAddress): string => {
  let addr = '';
  if (deliveryAddress?.addrLineOne) {
    addr += deliveryAddress?.addrLineOne;
  }
  if (deliveryAddress?.addrLineTwo) {
    addr += `, ${deliveryAddress?.addrLineTwo}`;
  }
  if (deliveryAddress?.city) {
    addr += `, ${deliveryAddress?.city}`;
  }
  if (deliveryAddress?.state) {
    addr += `, ${deliveryAddress?.state}`;
  }
  if (deliveryAddress?.postalCode) {
    addr += `, ${deliveryAddress?.postalCode}`;
  }
  if (deliveryAddress?.country) {
    addr += `, ${deliveryAddress?.country}`;
  }
  return addr;
};

export const getCheckoutCardProps = (product: ProductData) => {
  const productData = productDataTransformer(product);
  const productdata2: IProduct & { productQuantity: number; link: string } = {
    ...productData,
    productQuantity: 1,
    link: productData.mainLink,
  };
  return productdata2;
};

export const getSubscriptionTotal = (subscriptionDetails: SubscriptionResponse) => {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const products: any[] = subscriptionDetails?.products ?? [];
  const totalSummary = { totalAmount: 0, pointValue: 0, businessVolume: 0, totalProducts: 0, totalQuantity: 0 };
  products.forEach((product) => {
    const data = { ...productDataTransformer(product) };
    const productPrice = data.isBundle ? data.minPrice : data.productPrice;
    for (const entry of subscriptionDetails?.entries ?? []) {
      if (productId(entry.product.id ?? '') == productId(data.productId)) {
        totalSummary.totalAmount = totalSummary.totalAmount + (productPrice ?? 0) * entry.quantity;
        totalSummary.pointValue = totalSummary.pointValue + parseInt(data.pointValue) * entry.quantity;
        totalSummary.businessVolume = totalSummary.businessVolume + parseInt(data.businessVolume) * entry.quantity;
        totalSummary.totalProducts = totalSummary.totalProducts + 1;
        totalSummary.totalQuantity = totalSummary.totalQuantity + entry.quantity;
        break;
      }
    }
  });

  if (!subscriptionDetails.plan?.isFreeDelivery && subscriptionDetails.deliveryOption?.deliveryCharge) {
    totalSummary.totalAmount = totalSummary.totalAmount + parseInt(subscriptionDetails.deliveryOption?.deliveryCharge);
  }
  return totalSummary;
};

// const derivedAvailabilityStatus = (productData: ProductData): StockStatus => {
//   if (isBundle(productData)) {
//     return productData?.bundle?.derivedAvailabilityStatus as StockStatus;
//   } else {
//     return productData?.items?.[0]?.inventory?.[0]?.derivedAvailabilityStatus as StockStatus;
//   }
// };

export const fulfillmentPayload = (
  subscriptionDetails: SubscriptionResponse,
  postCode: string,
  country: string,
  fulfillmentTypeId?: string,
) => {
  const fulfillmentRequest: GetFulfillmentProps = {
    payload: {
      id: subscriptionDetails.id || '',
      fulfillmentTypeId: fulfillmentTypeId,
      address: {
        postCode: postCode,
        country: country,
      },
      ...transformCheckoutEntriesLineItems(subscriptionDetails.entries),
    },
  };
  return fulfillmentRequest;
};

export const transformCheckoutEntriesLineItems = (entries?: Array<any>, subscriptionId?: string) => {
  const lineItems: Array<LineItem> = [];
  const bundleItems: Array<BundleItem> = [];
  entries?.map((entry) => {
    entry?.bundleSlots?.length
      ? entry.bundleSlots.map((bundleSlot: BundleSlot) => {
          const transformedLineItems = transformCheckoutEntriesLineItems(bundleSlot?.entries);
          bundleItems.push({
            bundleId: entry?.product?.id,
            bundleQuantity: entry?.quantity,
            lineItems: transformedLineItems.lineItems,
          });
        })
      : lineItems.push({
          lineId: subscriptionId,
          itemId: entry?.product?.id,
          itemQuantity: entry?.quantity,
          lineAmount: {
            amount: entry?.totalPrice?.value,
            currency: entry?.product?.currency || '',
          },
          product: {
            productId: entry?.product?.id,
            hazardCode: entry?.hazardAttributes?.hazardCode,
            dimension: {
              unit: entry?.product?.productDimensions?.length?.unit,
              length: entry?.product?.productDimensions?.length?.value,
              width: entry?.product?.productDimensions?.width?.value,
              height: Number(entry?.product?.productDimensions?.height?.unit),
            },
            weight: entry?.product?.productDimensions?.weight,
          },
        });
  });
  return { lineItems, bundleItems };
};

export const calculateTotalPrice = (productData: ProductData, subscription: SubscriptionResponse): number => {
  const product = { ...productDataTransformer(productData) };
  let amount = 0;
  // @TODO: We have added price calculation logic only for Fixed Bundle.
  if (product.isBundle) {
    amount = (product.bundle?.minPrice || 1) * (subscription.entries ? subscription.entries[0].quantity : 1);
  } else {
    amount = product.productPrice * (subscription.entries ? subscription.entries[0].quantity : 1);
  }
  return amount;
};

export const transformShippingFee = (
  allocations: FulfillmentTypeCollection,
  totalPrice: number,
  isFreeDelivery: boolean,
): DeliveryOption => {
  let minimumNumberOfDays = 1,
    maximumNumberOfDays = 1,
    deliveryCharge = '0',
    id = '';
  if (allocations.fulfillmentTypes && allocations.fulfillmentTypes.length) {
    for (const fulfillment of allocations.fulfillmentTypes) {
      if (fulfillment.id == 'homedelivery') {
        const standardDelivery = fulfillment.serviceLevels?.filter((item: any) => item.id == 'standard');
        if (standardDelivery && standardDelivery.length) {
          id = standardDelivery[0].id as string;
          minimumNumberOfDays = standardDelivery[0].minimumNumberOfDays as number;
          maximumNumberOfDays = standardDelivery[0].maximumNumberOfDays as number;
          if (!isFreeDelivery && standardDelivery[0].fees && standardDelivery[0].fees.length > 0) {
            deliveryCharge = getServiceFee(standardDelivery[0].fees, totalPrice);
          }
          break;
        }
      }
    }
  }
  return { id, minimumNumberOfDays, maximumNumberOfDays, deliveryCharge };
};

const getServiceFee = (fees: any, totalPrice: number): string => {
  const nearestThreshold = fees
    .filter((fee: any) => fee.minimumThresholdAmount.amount < totalPrice)
    .sort((fee1: any, fee2: any) => fee1.minimumThresholdAmount.amount - fee2.minimumThresholdAmount.amount);

  return `${nearestThreshold[nearestThreshold.length - 1]?.serviceFee.amount ?? 0}`;
};

export const subscriptionListTransformer = (subscriptions: SubscriptionResponse[]) => {
  type prop = {
    subscriptionId: string;
    subscriptinCode: string;
    startDate: string;
    shipmentCount: string;
    profileCode: string;
    planCode: string;
    status?: SubscriptionResponseStatusEnum;
    version: number;
  };

  const subscriptionList: prop[] = [];
  subscriptions.forEach((subscription: SubscriptionResponse) => {
    subscriptionList.push({
      subscriptionId: subscription?.id ?? '',
      subscriptinCode: subscription.code ?? '',
      startDate: subscription.startDate?.toString() ?? '',
      shipmentCount: `${subscription.completedShipments ?? 0}/${subscription.totalShipments ?? 0}`,
      profileCode: subscription.profile?.id ?? '',
      planCode: subscription.plan?.code ?? '',
      status: subscription.status,
      version: subscription.version,
    });
  });

  return subscriptionList;
};

export const subscriptionProductDataTransformer = (productData: ProductData) => {
  type prop = {
    productId: string;
    itemNumber?: string;
    minQuantity: number;
    maxQuantity: number;
    productType?: string;
    productCode?: string;
    imageUrl?: string;
    imageAlt?: string;
    productName: string;
  };

  const _product = productDataTransformer(productData);
  const productOptionsItem: prop = {
    productId: productData.id,
    minQuantity: productData?.items[0]?.minOrderQuantity ?? 1,
    maxQuantity: productData?.items[0]?.maxOrderQuantity ?? 1,
    imageUrl: _product.mainLink,
    imageAlt: _product.alt,
    productType: _product.productType,
    productCode: productData?.aliasCode,
    productName: _product.productName,
    itemNumber: _product.productId,
  };

  return productOptionsItem;
};

const transformContact = (deliveryAddress: DeliveryAddress) => {
  let contact = '';
  if (deliveryAddress.recipientFirstName) {
    contact += deliveryAddress.recipientFirstName;
  }
  if (deliveryAddress.recipientLastName) {
    contact += ` ${deliveryAddress.recipientLastName}`;
  }
  return contact;
};

export const homeDeliveryDataTransformer = (deliveryAddress: DeliveryAddress) => ({
  contact: transformContact(deliveryAddress),
  address: transformAddress(deliveryAddress),
  deliveryInstructions: deliveryAddress.deliveryInstructions,
  phone: deliveryAddress.phone,
  country: deliveryAddress.country,
});

export const shippingDetailsDataTransformer = (fulfillment: Fulfillment) => {
  let shippingData: FulfillmentType | undefined = undefined;

  if (fulfillment.id === FulfillmentTypeIdEnum.HOME_DELIVERY) {
    shippingData = {
      id: fulfillment.id as FulfillmentTypeIdEnum,
      homeAddress: fulfillment.address ? homeDeliveryDataTransformer(fulfillment.address) : {},
    };
  } else if (fulfillment.id === FulfillmentTypeIdEnum.BOPIS) {
    shippingData = {
      id: fulfillment.id as FulfillmentTypeIdEnum,
      storeAddress: {
        serviceLocation: fulfillment.serviceLocation,
      },
    };
  }

  return shippingData;
};

export function removeBlankAttributes(obj: AppState) {
  const result: AppState = {};
  for (const key in obj) {
    if (obj[key] !== null && obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}
